import React, { useEffect, useState } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { supabaseService } from '../lib/supabase';

interface Props {
  children: React.ReactNode;
}

export const SupabaseConnectionHandler: React.FC<Props> = ({ children }) => {
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'connecting'>(
    supabaseService.getCurrentStatus()
  );
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const subscription = supabaseService.getConnectionStatus$().subscribe(status => {
      setConnectionStatus(status);
      if (status === 'disconnected') {
        setRetryCount(prev => prev + 1);
      } else if (status === 'connected') {
        setRetryCount(0);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleRetryConnection = async () => {
    setConnectionStatus('connecting');
    await supabaseService.reconnect();
  };

  if (connectionStatus === 'connecting') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Connecting to database...</p>
        </div>
      </div>
    );
  }

  if (connectionStatus === 'disconnected') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white p-8 rounded-lg shadow-lg">
            <div className="flex justify-center mb-4">
              <AlertTriangle className="h-12 w-12 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-center text-gray-900 mb-4">
              Database Connection Error
            </h1>
            <p className="text-gray-600 text-center mb-6">
              Unable to connect to the database. Please check your connection and try again.
            </p>
            <div className="flex justify-center">
              <button
                onClick={handleRetryConnection}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry Connection
              </button>
            </div>
            {retryCount > 0 && (
              <p className="text-sm text-gray-500 text-center mt-4">
                Retry attempt: {retryCount}/3
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};